import { useCallback, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { errorToUserFacingMessage } from '../api/apiErrors';
import { buildVSMEFormUrl, createVSMEReport, generateVSMEReport } from '../../../companies/src/utils/vsmeApi';
import { vsmeDossierRelativePath } from '@tpct/mission-core/utils/vsmePortalRoutes';
import type { MissionSummary } from '../types/missionPortal';

type UseMissionLifecycleActionsArgs = {
  hubBaseUrl: string;
  nonce: string;
  missionId: number;
  summary: MissionSummary | null;
  onNavigate: (path: string) => void;
  refreshSummary: () => Promise<void>;
  setSummary: (summary: MissionSummary | null) => void;
  setError: (message: string) => void;
};

type UseMissionLifecycleActionsResult = {
  busyAction: string | null;
  applyLifecycleAction: (action: string) => Promise<void>;
  resumeVsmeWork: () => Promise<void>;
};

export const useMissionLifecycleActions = ({
  hubBaseUrl,
  nonce,
  missionId,
  summary,
  onNavigate,
  refreshSummary,
  setSummary,
  setError,
}: UseMissionLifecycleActionsArgs): UseMissionLifecycleActionsResult => {
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const resumeVsmeWork = useCallback(async (): Promise<void> => {
    if (summary === null || busyAction !== null) {
      return;
    }

    if (summary.mission.state === 'archived') {
      setError(__('Cette mission est archivée et conservée. Elle ne peut pas être rouverte dans le formulaire VS.'));
      return;
    }

    if (summary.mission.state === 'closed') {
      setError(__('Cette mission est clôturée. Elle doit être rouverte avant de reprendre le formulaire VS.'));
      return;
    }

    setBusyAction('resume_report');
    setError('');

    try {
      const openVsmeForm = (reportId: string): void => {
        window.open(
          buildVSMEFormUrl(reportId, summary.mission.company_id, summary.mission.id),
          '_blank',
          'noopener,noreferrer',
        );
        setBusyAction(null);
      };

      if (summary.linked_report_id !== null) {
        openVsmeForm(String(summary.linked_report_id));
        return;
      }

      const report = await createVSMEReport(
        summary.mission.company_id,
        summary.mission.period_start,
        summary.mission.period_end,
        summary.mission.id,
      );

      openVsmeForm(String(report.id));
    } catch (e) {
      setError(errorToUserFacingMessage(e, __('Impossible de démarrer le formulaire VS.')));
      setBusyAction(null);
    }
  }, [busyAction, setError, summary]);

  const applyLifecycleAction = useCallback(async (action: string): Promise<void> => {
    if (busyAction !== null) {
      return;
    }

    if (action === 'resume_report') {
      await resumeVsmeWork();
      return;
    }

    if (action === 'review_diligences' || action === 'open_work_folder') {
      if (summary?.linked_report_id === null || summary?.linked_report_id === undefined) {
        setError(__('Aucun rapport VS n’est rattaché à cette mission.'));
        return;
      }

      onNavigate(vsmeDossierRelativePath(summary.mission.id));
      return;
    }

    if (action === 'generate_report') {
      if (summary?.linked_report_id === null || summary?.linked_report_id === undefined) {
        setError(__('Aucun rapport VS n’est rattaché à cette mission.'));
        return;
      }

      setBusyAction(action);
      setError('');

      try {
        await generateVSMEReport(summary.linked_report_id);
        await refreshSummary();
      } catch (e) {
        setError(errorToUserFacingMessage(e, __('Impossible de générer le rapport.')));
      } finally {
        setBusyAction(null);
      }

      return;
    }

    setBusyAction(action);
    setError('');

    try {
      const response = await fetchWithNonce(`${hubBaseUrl}missions/${missionId}/lifecycle`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({ action }),
      });

      if (!response.ok) {
        setError(await parseApiError(response));
        return;
      }

      const payload = (await response.json()) as { data?: MissionSummary };
      if (payload.data !== undefined) {
        setSummary(payload.data);
      }
    } catch (e) {
      setError(errorToUserFacingMessage(e, __('Action impossible.')));
    } finally {
      setBusyAction(null);
    }
  }, [
    busyAction,
    hubBaseUrl,
    missionId,
    nonce,
    onNavigate,
    refreshSummary,
    resumeVsmeWork,
    setError,
    setSummary,
    summary,
  ]);

  return {
    busyAction,
    applyLifecycleAction,
    resumeVsmeWork,
  };
};
